import { ORGANIZATION_ID } from "./constants"
import { getJsonLdLanguage, type JsonLdNode } from "./core"

export interface DataCatalogOptions {
  url: string
  name: string
  description?: string
  inLanguage?: string
  /** 所属页面（CollectionPage）@id，默认 `${url}#collectionpage` */
  mainEntityOfPageId?: string
  organizationId?: string
}

/** DataCatalog 节点（品目页的价格目录）。@id = canonical + #datacatalog。 */
export function buildDataCatalogJsonLd(options: DataCatalogOptions): JsonLdNode {
  const {
    url,
    name,
    description,
    inLanguage = "en",
    mainEntityOfPageId = `${url}#collectionpage`,
    organizationId = ORGANIZATION_ID,
  } = options
  return {
    "@type": "DataCatalog",
    "@id": `${url}#datacatalog`,
    url,
    name,
    inLanguage: getJsonLdLanguage(inLanguage),
    publisher: { "@id": organizationId },
    mainEntityOfPage: { "@id": mainEntityOfPageId },
    ...(description ? { description } : {}),
  }
}

export interface DatasetDistribution {
  url: string
  encodingFormat: string
}

export interface DatasetOptions {
  url: string
  name: string
  description?: string
  inLanguage?: string
  /** 所属 DataCatalog @id，默认取品目页 catalog：`{品目url}#datacatalog` */
  catalogId?: string
  /** 统计口径，默认 SMM Benchmark Price Assessment */
  measurementTechnique?: string
  /** 数据列名，如 ["Name","Average","Unit","High","Low","Change","Date"] */
  variableMeasured?: string[]
  /** ISO8601 时间，如 "2026-07-03T09:30:00Z" */
  dateModified?: string
  /** 下载分发（pdf / xlsx 等） */
  distribution?: DatasetDistribution[]
  organizationId?: string
}

/** Dataset 节点（价格数据集）。@id = canonical + #dataset。 */
export function buildDatasetJsonLd(options: DatasetOptions): JsonLdNode {
  const {
    url,
    name,
    description,
    inLanguage = "en",
    catalogId,
    measurementTechnique = "SMM Benchmark Price Assessment",
    variableMeasured,
    dateModified,
    distribution,
    organizationId = ORGANIZATION_ID,
  } = options
  return {
    "@type": "Dataset",
    "@id": `${url}#dataset`,
    url,
    name,
    inLanguage: getJsonLdLanguage(inLanguage),
    publisher: { "@id": organizationId },
    ...(description ? { description } : {}),
    ...(catalogId ? { includedInDataCatalog: { "@id": catalogId } } : {}),
    measurementTechnique,
    ...(variableMeasured?.length
      ? { variableMeasured: variableMeasured.map((name) => ({ "@type": "PropertyValue", name })) }
      : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(distribution?.length
      ? {
          distribution: distribution.map((item) => ({
            "@type": "DataDownload",
            url: item.url,
            encodingFormat: item.encodingFormat,
          })),
        }
      : {}),
  }
}
